import { memo, useState, useCallback, useRef, useEffect } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { Tooltip } from '@mui/material';
import { StyledIconButton, StyledInput, Wrapper } from './CollapsibleSearchField.styles';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import { useDebounce } from 'utils/hooks';
import { useTranslation } from 'react-i18next';

interface Props {
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  dataTestId?: string;
}

const CollapsibleSearchFieldBase = ({ value: valueProp, onChange, placeholder, dataTestId }: Props) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [value, setValue] = useState(valueProp ?? '');
  const debouncedValue = useDebounce<string>(value, 500);
  const inputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const next = valueProp ?? '';
    setValue((current) => (current !== next ? next : current));
  }, [valueProp]);

  const handleExpand = useCallback(() => {
    setExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleCollapse = useCallback(() => {
    if (!value) {
      setExpanded(false);
    }
  }, [value]);

  const handleClear = useCallback(() => {
    setValue('');
    setExpanded(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        handleClear();
      }
    },
    [handleClear]
  );

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  }, []);

  useEffect(() => {
    onChangeRef.current?.({ target: { value: debouncedValue } } as ChangeEvent<HTMLInputElement>);
  }, [debouncedValue]);

  return (
    <Wrapper expanded={expanded}>
      <Tooltip title={expanded ? '' : t('common.search.records')}>
        <StyledIconButton onClick={expanded ? undefined : handleExpand} disableRipple={expanded} size="small">
          <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </StyledIconButton>
      </Tooltip>
      {expanded && (
        <>
          <StyledInput
            inputRef={inputRef}
            value={value}
            onChange={handleChange}
            onBlur={handleCollapse}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            inputProps={{ 'data-testid': dataTestId ?? 'collapsible-search-field' }}
          />
          {value && (
            <StyledIconButton onClick={handleClear} size="small">
              <CloseIcon fontSize="small" sx={{ color: 'text.secondary', width: 16, height: 16 }} />
            </StyledIconButton>
          )}
        </>
      )}
    </Wrapper>
  );
};

export const CollapsibleSearchField = memo(CollapsibleSearchFieldBase) as typeof CollapsibleSearchFieldBase;
